import { getRequestKey } from '../utils/requestKey';
import { determineRefereePattern } from '../utils/refereePattern';
import { Card, Badge, Button, IconButton, Checkbox, Icon } from '../design-system/components';

const STATUS_ACCENT = {
  Scheduled: 'var(--success)',
  Processing: 'var(--secondary)',
  Requested: 'var(--accent)',
  Denied: 'var(--danger)',
  Cancelled: 'var(--border-strong)',
};

const STATUS_TONE = {
  Scheduled: 'success',
  Processing: 'info',
  Requested: 'warning',
  Denied: 'danger',
  Cancelled: 'neutral',
};

const GAME_TYPE_TONE = {
  Friendly: 'success',
  League: 'info',
  Tournament: 'warning',
};

/**
 * Request card component
 * Shows request details in standard mode
 */
export const RequestCard = ({
  request,
  isStale,
  daysInProcessing,
  isSelected,
  onSelect,
  onStatusChange,
  onUpdateAdminNotes,
  onClone,
  onUnassign,
  onMarkScheduled,
  onCancel,
  onResendEmail,
  onSetRefereePattern
}) => {
  const status = request['Request Status'];
  const refereePattern = request['Referee Pattern'] || '';
  const isNoRefs = refereePattern === 'No Refs';
  // What the pattern would be without the override — the target for "Undo".
  const autoPattern = determineRefereePattern(
    request['Game Type'],
    request['Age Group'],
    request['Field Size']
  );
  // Nothing to undo when the game type already implies No Refs (State League,
  // National 1, Intraclub) — the toggle would just set the same value back.
  const showRefsToggle = Boolean(onSetRefereePattern) && (!isNoRefs || autoPattern !== 'No Refs');

  const handleDragStart = (e) => {
    e.dataTransfer.setData('requestKey', getRequestKey(request));
    e.dataTransfer.effectAllowed = 'move';
  };

  const accent = STATUS_ACCENT[status] || 'var(--border-strong)';
  const ring = isSelected
    ? '0 0 0 3px var(--ink), '
    : isStale
    ? '0 0 0 3px var(--danger-soft), '
    : '';

  return (
    <Card
      draggable
      onDragStart={handleDragStart}
      onClick={onSelect}
      accent={accent}
      padding="var(--space-3)"
      style={{ boxShadow: `${ring}var(--shadow-sm)`, cursor: 'move', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}
    >
      {isStale && (
        <div className="mb-2 flex items-center gap-2" style={{ padding: 'var(--space-2)', background: 'var(--warning-soft)', borderLeft: '4px solid var(--warning)' }}>
          <Icon name="alert-triangle" size={18} color="var(--swed-gold-deep)" />
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 'var(--fw-bold)', fontSize: 'var(--text-xs)', letterSpacing: 'var(--ls-wide)', color: 'var(--swed-gold-deep)' }}>
              Stale request &mdash; {daysInProcessing} days in Processing
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--swed-gold-deep)' }}>
              Please complete or reassign this request
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2 flex-wrap" style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>
          {request['Game Number'] && (
            <span style={{ fontFamily: 'var(--font-mono)', background: 'var(--ink)', color: 'var(--text-inverse)', padding: '4px 10px', fontWeight: 'var(--fw-bold)', borderRadius: 'var(--radius-sm)' }}>
              #{request['Game Number']}
            </span>
          )}
          <span>{request['Team Name'] || 'Unnamed'}</span>
          {request['Game Type'] && (
            <Badge tone={GAME_TYPE_TONE[request['Game Type']] || 'neutral'} variant="soft">
              {request['Game Type']}
            </Badge>
          )}
        </div>
        <Badge tone={STATUS_TONE[status] || 'neutral'} variant="solid">
          {status}
        </Badge>
      </div>

      <div className="space-y-1" style={{ color: 'var(--text-body)' }}>
        {request['Contact Name'] && (
          <div style={{ color: 'var(--text-muted)' }}>
            <strong>Requester:</strong> {request['Contact Name']}
          </div>
        )}
        <div>
          <strong>Teams:</strong> {request['Home Team']} vs {request['Away Team']}
        </div>
        <div>
          <strong>Size:</strong> {request['Field Size']} |
          <strong> Age:</strong> {request['Age Group']}
        </div>
        <div className="flex items-center gap-1" style={{ fontFamily: 'var(--font-mono)' }}>
          <Icon name="map-pin" size={14} color="var(--text-muted)" />
          <strong className="not-italic" style={{ fontFamily: 'var(--font-sans)' }}>Venue:</strong> {request['Requested Venue']}
        </div>
        <div className="flex items-center gap-1" style={{ fontFamily: 'var(--font-mono)' }}>
          <Icon name="calendar" size={14} color="var(--text-muted)" />
          <strong className="not-italic" style={{ fontFamily: 'var(--font-sans)' }}>Date/Time:</strong> {request['Requested Date']} at {request['Preferred Time']}
        </div>

        {/* Referee pattern — teams sometimes request no referees, so let the
            assignor flip the pattern straight from the card. */}
        <div className="flex items-center gap-2 flex-wrap">
          <strong>Refs:</strong>
          <Badge tone={isNoRefs ? 'neutral' : 'info'} variant="soft">
            {refereePattern || 'Not set'}
          </Badge>
          {showRefsToggle && (
            <Button
              size="sm"
              variant={isNoRefs ? 'secondary' : 'outline'}
              iconLeft={isNoRefs ? 'check' : 'x'}
              style={{ padding: '4px 10px', fontSize: 'var(--text-xs)' }}
              title={
                isNoRefs
                  ? 'Restore the auto-assigned referee pattern'
                  : 'Team requested no referees for this game'
              }
              onClick={(e) => {
                e.stopPropagation();
                onSetRefereePattern(request, isNoRefs ? autoPattern : 'No Refs');
              }}
            >
              {isNoRefs ? 'Undo No Refs' : 'No Refs'}
            </Button>
          )}
        </div>

        {request['Special Notes'] && (
          <div className="mt-2 relative group" style={{ padding: 'var(--space-2)', background: 'var(--surface-sunken)', borderLeft: '4px solid var(--warning)' }}>
            <div className="flex items-start gap-1">
              <Icon name="file-text" size={14} color="var(--text-muted)" style={{ marginTop: 2 }} />
              <div>
                <strong>Notes:</strong>{' '}
                <span className="group-hover:hidden">
                  {request['Special Notes'].split(' ').slice(0, 5).join(' ')}
                  {request['Special Notes'].split(' ').length > 5 && '...'}
                </span>
                <span className="hidden group-hover:inline">
                  {request['Special Notes']}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 relative group" style={{ padding: 'var(--space-2)', background: 'var(--surface-sunken)', borderLeft: '4px solid var(--info)' }}>
          <div className="flex justify-between items-start gap-1">
            <div className="flex-1 flex items-start gap-1">
              <Icon name="clipboard" size={14} color="var(--text-muted)" style={{ marginTop: 2 }} />
              <div>
                <strong>Assignor Notes:</strong>{' '}
                {request['Admin Notes'] ? (
                  <>
                    <span className="group-hover:hidden">
                      {request['Admin Notes'].split(' ').slice(0, 5).join(' ')}
                      {request['Admin Notes'].split(' ').length > 5 && '...'}
                    </span>
                    <span className="hidden group-hover:inline">
                      {request['Admin Notes']}
                    </span>
                  </>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Click to add notes</span>
                )}
              </div>
            </div>
            <IconButton
              icon="pencil"
              size="sm"
              variant="ghost"
              label="Edit assignor notes"
              onClick={(e) => {
                e.stopPropagation();
                const currentNotes = request['Admin Notes'] || '';
                const newNotes = prompt('Edit Assignor Notes:', currentNotes);
                if (newNotes !== null) {
                  onUpdateAdminNotes(request, newNotes);
                }
              }}
            />
          </div>
        </div>

        {(status === 'Processing' || status === 'Scheduled') && request.SlotID && (
          <div className="mt-2" style={{ padding: 'var(--space-2)', background: 'var(--success-soft)', borderLeft: '4px solid var(--success)' }}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-1" style={{ fontFamily: 'var(--font-sans)', fontWeight: 'var(--fw-bold)', fontSize: 'var(--text-xs)', letterSpacing: 'var(--ls-wide)', color: 'var(--folk-green-deep)' }}>
                  <Icon name="check-circle" size={14} color="var(--folk-green-deep)" /> Assigned field:
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-strong)' }}>{request.SlotID}</div>
              </div>
              <IconButton
                icon="x"
                size="sm"
                variant="solid"
                label="Unassign"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Unassign this request? This will revert it to Requested status.')) onUnassign(request);
                }}
              />
            </div>
          </div>
        )}

        {status === 'Processing' && request.SlotID && (
          <div className="mt-2" onClick={(e) => e.stopPropagation()} style={{ padding: 'var(--space-2)', background: 'var(--success-soft)', border: '2px solid var(--success)', borderRadius: 'var(--radius-md)' }}>
            <Checkbox
              label="Mark as Scheduled"
              onChange={(checked) => {
                if (checked) onMarkScheduled(request);
              }}
            />
          </div>
        )}

        {/* Re-send Confirmation Email */}
        {status === 'Scheduled' && (
          <Button
            block
            variant="accent"
            size="sm"
            iconLeft="mail"
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Re-send confirmation email for this game?')) {
                onResendEmail(request);
              }
            }}
          >
            Re-send Confirmation Email
          </Button>
        )}

        {/* Decline Button */}
        {(status === 'Requested' || status === 'Processing') && (
          <Button
            block
            variant="danger"
            size="sm"
            iconLeft="x"
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              const reason = prompt('Reason for declining this request:');
              if (reason !== null && reason.trim() !== '') {
                onStatusChange(request, 'Denied', reason.trim());
              }
            }}
          >
            Decline Request
          </Button>
        )}

        {/* Clone Button for Denied/Cancelled Requests */}
        {(status === 'Denied' || status === 'Cancelled') && (
          <Button
            block
            variant="secondary"
            size="sm"
            iconLeft="copy"
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              onClone(request);
            }}
          >
            Clone to New Request
          </Button>
        )}

        {/* Cancel Button — available from Requested, Processing, Scheduled */}
        {(status === 'Requested' || status === 'Processing' || status === 'Scheduled') && (
          <Button
            block
            variant="outline"
            size="sm"
            iconLeft="x"
            className="mt-2"
            title="Cancel this request"
            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={(e) => {
              e.stopPropagation();
              const reason = prompt('Cancellation reason (required):');
              if (reason !== null && reason.trim() !== '') {
                onCancel(request, reason.trim());
              }
            }}
          >
            Cancel Request
          </Button>
        )}
      </div>
    </Card>
  );
};
